import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Zap, CreditCard, Star, Crown, ArrowRight, CheckCircle, TrendingUp, Coffee, Dumbbell, Plane, Globe, Lock, Clock, MessageCircle, Home, FileText, Wrench } from 'lucide-react';
import { VerifiedBadgeIcon, ProBadgeIcon } from '../components/ui/Badges';
import { FaqSection } from '../components/sections/FaqSection';

interface TenantLandingPageProps {
  navigateTo: (page: string, data?: any) => void;
  onJoinWaitingList: () => void;
}

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

export const TenantLandingPage = ({ navigateTo, onJoinWaitingList }: TenantLandingPageProps) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-black selection:bg-amber-500/30 font-sans overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[600px] lg:min-h-[700px] flex items-center overflow-hidden">
        {/* Background Gradients/Elements */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="container relative z-10 w-full mx-auto px-6 pt-6 pb-20 lg:pb-32 lg:pt-18 flex flex-col lg:flex-row items-center justify-between gap-12">
          {/* Text Content */}
          <div className="flex-1 text-center lg:text-left">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.h1 variants={fadeUp} className="text-4xl lg:text-[72px] font-extrabold tracking-tighter leading-[1.05] mb-6 px-6 md:px-0">
              The All Access Pass that elevates every <br className="hidden lg:block" />
              day
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg lg:text-xl text-gray-600 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed font-light">
              For better everyday renting and beyond, unlock lifestyle subscriptions and higher trust ratings.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <button onClick={onJoinWaitingList} className="w-2/3 sm:w-auto px-8 py-4 rounded-full bg-black text-white text-sm font-bold text-lg hover:bg-gray-800 transition-transform active:scale-95 flex items-center justify-center gap-2">
                Get access
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* 3D Card Visual */}
        <motion.div 
          className="flex-1 z-10 flex justify-center lg:justify-end perspective-1000"
          initial={{ opacity: 0, scale: 0.9, rotateY: 20 }}
          animate={{ opacity: 1, scale: 1, rotateY: -5 }}
          transition={{ duration: 1.2, delay: 0.2, type: "spring", bounce: 0.4 }}
        >
          <img 
            src="https://firebasestorage.googleapis.com/v0/b/qualified-root-298415.firebasestorage.app/o/media%2F1775829645021_Gemini_Generated_Image_3npw753npw753npw-Photoroom-Photoroom.png?alt=media&token=61cbf1cd-e6e1-4b15-87b2-1e7c75532542" 
            alt="Coleese Passport Card" 
            className="lg:-mb-48 lg:ml-10 w-full max-w-xl h-auto object-contain drop-shadow-2xl transform transition-transform hover:rotate-y-12 hover:rotate-x-12 duration-500"
            referrerPolicy="no-referrer"
          />
        </motion.div>
      </div>
    </section>

      {/* Core Features Section (White Background) */}
      <section className="-mt-44 lg:-mt-28 py-20 px-6 bg-white text-black rounded-t-[40px] lg:rounded-t-[80px] relative z-20">
        <motion.div 
          className="max-w-7xl mx-auto"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeUp} className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight mb-6">Everything you need to rent</h2>
            <p className="text-gray-600 text-base lg:text-lg max-w-3xl mx-auto font-light leading-relaxed">
              Coleese gives you the tools to build your renter profile, stand out to landlords, and manage your entire rental journey from one app.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Passport */}
            <motion.div variants={fadeUp} className="bg-[#f8f8f8] rounded-[32px] p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center mb-8 shadow-lg">
                 <Shield size={20} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">The All Access Pass</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Your ultimate renter resume. Combine your identity verification, credit score, and rental history into one powerful profile.
              </p>
            </motion.div>

            {/* Applications */}
            <motion.div variants={fadeUp} className="bg-[#f8f8f8] rounded-[32px] p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-full bg-[#2088ff] flex items-center justify-center mb-8 shadow-lg">
                <FileText size={20} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">1-Click Applications</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Apply to your dream homes instantly. Use your Passport to securely share your details with landlords without filling out endless forms.
              </p>
            </motion.div>

            {/* Wishlist */}
            <motion.div variants={fadeUp} className="bg-[#f8f8f8] rounded-[32px] p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#fd297b] to-[#ff655b] flex items-center justify-center mb-8 shadow-lg">
                <Home size={20} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Curated Wishlists</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Save your favorite properties, compare options, and keep track of places you want to apply to in your personal wishlist.
              </p>
            </motion.div>

            {/* Messaging */}
            <motion.div variants={fadeUp} className="bg-[#f8f8f8] rounded-[32px] p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-full bg-[#4687ff] flex items-center justify-center mb-8 shadow-lg">
                <MessageCircle size={20} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Direct Messaging</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Communicate directly with hosts and landlords securely within the app. Ask questions and schedule viewings easily.
              </p>
            </motion.div>

            {/* Lease Management */}
            <motion.div variants={fadeUp} className="bg-[#f8f8f8] rounded-[32px] p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-full bg-[#f47c20] flex items-center justify-center mb-8 shadow-lg">
                <CheckCircle size={20} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Lease Management Tools</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Access your active leases, view important documents, and keep track of your rental terms all in one digital dashboard.
              </p>
            </motion.div>

            {/* Maintenance */}
            <motion.div variants={fadeUp} className="bg-[#f8f8f8] rounded-[32px] p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 rounded-full bg-[#ff9900] flex items-center justify-center mb-8 shadow-lg">
                <Wrench size={20} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Maintenance Requests</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Log maintenance issues quickly, attach photos, and track the progress of repairs directly with your landlord.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Build Your Reputation Section (Cinematic Style) */}
      <section className="relative min-h-[600px] lg:min-h-[800px] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://firebasestorage.googleapis.com/v0/b/qualified-root-298415.firebasestorage.app/o/media%2Fpexels-shvets-production-8933550.jpg?alt=media&token=5fd69adf-9adf-4704-b2c6-f49a87e3b9ae" 
            alt="Renter at Home" 
            className="w-full h-full object-cover object-right"
            referrerPolicy="no-referrer"
          />
          {/* Dark Cinematic Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-l from-black/80 via-black/40 to-transparent lg:from-black/60 lg:via-transparent lg:to-black/80"></div>
          <div className="absolute inset-0 bg-black/20 lg:bg-transparent"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 w-full relative z-10 grid grid-cols-1 lg:grid-cols-2 lg:items-start gap-12">
          {/* Text Content */}
          <motion.div 
            className="text-white text-center lg:text-left"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          >
            <h2 className="text-4xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95] mb-8">
              Build your <br className="hidden md:block" />reputation
            </h2>
            <p className="text-lg lg:text-xl text-white/70 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
              Your Pass Score is updated dynamically based on your rental history, credit, and verified identity. A higher score means faster approvals and better homes.
            </p>
            <button 
              onClick={() => navigateTo('listings')}
              className="px-10 py-4 rounded-full bg-white text-black font-bold text-sm lg:text-base hover:bg-gray-100 transition-all hover:scale-105 active:scale-95 shadow-xl"
            >
              Explore Rentals
            </button>
          </motion.div>

          {/* Floating Reputation Card */}
          <motion.div 
            className="flex justify-center"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="w-full max-w-sm bg-black/60 backdrop-blur-xl border border-white/10 rounded-[32px] p-4 shadow-2xl">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden border border-white/20">
                    <img 
                      src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80" 
                      alt="Joe Leo Doe" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm lg:text-lg leading-tight">Joe Leo Doe</h4>
                    <p className="text-white/50 text-[10px] lg:text-xs font-semibold uppercase tracking-wider">Primary Applicant</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full border border-white/10">
                   <Shield size={14} className="text-white/80" />
                   <span className="text-white font-black text-sm">50</span>
                   <ArrowRight size={14} className="text-white/40 ml-1" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Badges Section */}
      <section className="py-20 px-6 bg-white text-center border-y border-white/5">
        <motion.div 
          className="max-w-6xl mx-auto"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
            <motion.h2 variants={fadeUp} className="text-3xl lg:text-5xl font-extrabold mb-8 tracking-tight">Your digital badge awaits</motion.h2>
            <motion.p variants={fadeUp} className="text-gray-400 text-lg mb-4 font-light max-w-3xl mx-auto">
                Stand out to landlords instantly. Your Coleese badge is displayed on your profile and applications, showing your trust tier based on your Pass Score.
            </motion.p>
            
            <motion.div variants={fadeUp} className="relative flex justify-center -mb-24 lg:-mb-36 z-10">
                <img 
                    src="https://firebasestorage.googleapis.com/v0/b/qualified-root-298415.firebasestorage.app/o/media%2F1775830091146_Gemini_Generated_Image_3ywpmr3ywpmr3ywp-Photoroom.png?alt=media&token=090e268b-f6bf-4b67-b4f2-d8ba56caffc1" 
                    alt="Coleese Digital Badges" 
                    className="w-full max-w-4xl h-auto object-contain drop-shadow-2xl"
                    referrerPolicy="no-referrer"
                />
            </motion.div>
        </motion.div>
      </section>

      {/* Highlights Section (White Background) */}
      <section className="py-20 px-6 bg-white text-black rounded-t-[40px] lg:rounded-t-[80px] relative z-20">
        <motion.div 
          className="max-w-7xl mx-auto"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
            <motion.h2 variants={fadeUp} className="text-3xl lg:text-4xl font-extrabold mb-14 text-center tracking-tight">Why tenants love Coleese</motion.h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                <motion.div variants={fadeUp} className="bg-[#f8f8f8] rounded-[32px] p-10 hover:shadow-lg transition-shadow">
                    <h3 className="text-2xl font-bold mb-4">Stand out instantly</h3>
                    <p className="text-gray-600 text-base leading-relaxed">Your Passport Score and Badge prove your reliability to landlords, helping you secure the best properties faster.</p>
                </motion.div>
                <motion.div variants={fadeUp} className="bg-[#f8f8f8] rounded-[32px] p-10 hover:shadow-lg transition-shadow">
                    <h3 className="text-2xl font-bold mb-4">No more repetitive forms</h3>
                    <p className="text-gray-600 text-base leading-relaxed">Fill out your details once. Apply to multiple properties with a single click using your secure Passport.</p>
                </motion.div>
                <motion.div variants={fadeUp} className="bg-[#f8f8f8] rounded-[32px] p-10 hover:shadow-lg transition-shadow">
                    <h3 className="text-2xl font-bold mb-4">Secure communication</h3>
                    <p className="text-gray-600 text-base leading-relaxed">Keep all your landlord communications in one place. No need to share your personal phone number.</p>
                </motion.div>
            </div>
        </motion.div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-6 text-center bg-white text-black border-t border-gray-100">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2 variants={fadeUp} className="text-3xl lg:text-4xl font-extrabold mb-8 tracking-tight max-w-4xl mx-auto">Ready to elevate your rental experience?</motion.h2>
          <motion.p variants={fadeUp} className="text-gray-600 text-base mb-8 font-light">Join the platform that puts your reputation first.</motion.p>
          <motion.button variants={fadeUp} onClick={onJoinWaitingList} className="px-10 py-5 rounded-full bg-black text-white font-bold text-lg hover:bg-gray-800 transition-transform active:scale-95">
            Get access
          </motion.button>
        </motion.div>
      </section>

      <FaqSection />
    </div>
  );
};

